/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// ============================================
// ComunicazioniSicurezzaModal - VERSIONE CORRETTA
// Fix: Posizione DOS usa index 0 o titolo
// ============================================

import React, { useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { toPhonetic, toPhoneticCallsign } from '../../utils/fraseologia';
import './AvvicinamentoModal.css';

interface ComunicazioniSicurezzaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ComunicazioniSicurezzaModal: React.FC<ComunicazioniSicurezzaModalProps> = ({ isOpen, onClose }) => {
  const mezziAerei = useAppStore((state) => state.mezziAerei);
  const coordinate = useAppStore((state) => state.coordinate);
  const coau = useAppStore((state) => state.coau);
  const addLogComunicazione = useAppStore((state) => state.addLogComunicazione);

  // Log apertura briefing sicurezza
  useEffect(() => {
    if (isOpen) {
      addLogComunicazione({
        tipo: 'sicurezza',
        mittente: (coau as any)?.nominativoDOS || 'DOS',
        destinatario: 'ALL STATIONS',
        messaggio: 'Briefing Comunicazioni di Sicurezza aperto — trasmissione dati operativi',
        priorita: 'normale',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // FILTRI MEZZI AEREI
  // Traffico Operante: posizioni specifiche
  const posizioniOperanti = [
    'Primo al Lancio',
    'Ha Lanciato',
    'In Rifornimento',
    'In Orbita',
    'Rientro Base',
    'May Day',
    'Annullato Lancio'
  ];
  
  const mezziOperanti = mezziAerei.filter(m => 
    posizioniOperanti.includes(m.posizioneOperativa)
  );

  // Traffico Addizionale: solo "In Avvicinamento"
  const mezziAddizionali = mezziAerei.filter(m => 
    m.posizioneOperativa === 'in_avvicinamento'
  );

  // Callsign dinamico: primo mezzo in avvicinamento o primo disponibile
  const mezzoAttivo =
    mezziAerei.find(m => m.posizioneOperativa === 'in_avvicinamento') ||
    mezziAerei[0];
  const callsignMezzo = mezzoAttivo ? toPhoneticCallsign(mezzoAttivo.sigla) : 'STAZIONE CHIAMANTE';
  const nominativoDOS = (coau as any)?.nominativoDOS;
  const callsignDOS = nominativoDOS ? toPhonetic(nominativoDOS) : 'DOS';

  // COORDINATE POSIZIONE DOS
  const posizioneDOS = coordinate.length > 0
    ? coordinate[0]
    : coordinate.find(c => c.titolo === 'Posizione DOS');

  // DATI COAU (assumendo struttura da COAUForm.tsx)
  const ostacoliAerei = (coau as any)?.ostacoliCompilati || [];
  const elettrodotto = (coau as any)?.elettrodotto || null;
  const ostacoliDirezioni = (coau as any)?.ostacoliDirezioni || {};
  const visibilitaNote = (coau as any)?.visibilitaNote || '';

  const ostacoliTerra = (coau as any)?.ostacoliTerraCompilati || [];
  const ostacoliTerraDirezioni = (coau as any)?.ostacoliTerraDirezioni || {};

  const vento = {
    intensita: (coau as any)?.intensitaVento || '',
    direzione: (coau as any)?.direzioneVento || null
  };

  return (
    <div className="avvicinamento-modal-overlay" onClick={onClose}>
      <div className="avvicinamento-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="avvicinamento-modal-header">
          <h3>📡 Comunicazioni di Sicurezza</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="avvicinamento-modal-body">
          {/* TRAFFICO OPERANTE */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">✈️</div>
              <h4>Traffico Operante</h4>
            </div>
            <div className="step-content">
              {/* Callsign fisso prima del traffico operante */}
              <div className="callsign-header" style={{
                backgroundColor: '#ff6b35',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '15px',
                fontWeight: 'bold',
                fontSize: '16px',
                color: '#fff',
                textAlign: 'center',
                letterSpacing: '1px'
              }}>
                {callsignMezzo} {callsignDOS}:
              </div>
              <div className="info-box">
                {mezziOperanti.length === 0 ? (
                  <p>Nessun traffico aereo operante.</p>
                ) : (
                  <div className="mezzi-list">
                    {mezziOperanti.map((mezzo) => (
                      <div key={mezzo.id} className="mezzo-item">
                        <strong>{mezzo.sigla}</strong> - {mezzo.tipo === 'ala_fissa' ? 'Canadair' : 'Elicottero'} - <span className="posizione-badge">{mezzo.posizioneOperativa}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* TRAFFICO ADDIZIONALE */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">✈️</div>
              <h4>Traffico Addizionale</h4>
            </div>
            <div className="step-content">
              <div className="info-box">
                {mezziAddizionali.length === 0 ? (
                  <p>Nessun traffico aereo addizionale segnalato.</p>
                ) : (
                  <div className="mezzi-list">
                    {mezziAddizionali.map((mezzo) => (
                      <div key={mezzo.id} className="mezzo-item">
                        <strong>{mezzo.sigla}</strong> - {mezzo.tipo === 'ala_fissa' ? 'Canadair' : 'Elicottero'} - <span className="posizione-badge">In Avvicinamento</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* OSTACOLI AEREI */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">⚡</div>
              <h4>Ostacoli Aerei</h4>
            </div>
            <div className="step-content">
              <div className="info-box">
                {ostacoliAerei.length === 0 ? (
                  <p>Nessun dato segnalato.</p>
                ) : (
                  <div className="ostacoli-list">
                    {ostacoliAerei.map((ostacolo: string, index: number) => (
                      <div key={index} className="ostacolo-item">
                        <strong>{ostacolo}</strong>
                        {ostacolo === 'Elettrodotti' && elettrodotto && (
                          <div className="ostacolo-details">
                            • Stato: {elettrodotto.stato}<br/>
                            {elettrodotto.tensione && `• Tensione: ${elettrodotto.tensione}`}<br/>
                            {elettrodotto.direzione && `• Direzione: ${elettrodotto.direzione}`}
                          </div>
                        )}
                        {ostacolo !== 'Elettrodotti' && ostacoliDirezioni[ostacolo] && (
                          <div className="ostacolo-details">
                            • Direzione: {ostacoliDirezioni[ostacolo]}
                          </div>
                        )}
                        {ostacolo === 'Visibilità' && visibilitaNote && (
                          <div className="ostacolo-details">
                            • Note: {visibilitaNote}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* OSTACOLI A TERRA */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">🗻</div>
              <h4>Ostacoli a Terra</h4>
            </div>
            <div className="step-content">
              <div className="info-box">
                {ostacoliTerra.length === 0 ? (
                  <p>Nessun dato segnalato.</p>
                ) : (
                  <div className="ostacoli-list">
                    {ostacoliTerra.map((ostacolo: string, index: number) => (
                      <div key={index} className="ostacolo-item">
                        <strong>{ostacolo}</strong>
                        {ostacoliTerraDirezioni[ostacolo] && (
                          <div className="ostacolo-details">
                            • Direzione: {ostacoliTerraDirezioni[ostacolo]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* VENTO */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">💨</div>
              <h4>Vento</h4>
            </div>
            <div className="step-content">
              <div className="info-box">
                {!vento.intensita && !vento.direzione ? (
                  <p>Nessun dato segnalato.</p>
                ) : (
                  <div className="vento-info">
                    {vento.intensita && (
                      <div className="vento-item">
                        <strong>Intensità:</strong> {vento.intensita}
                      </div>
                    )}
                    {vento.direzione && (
                      <div className="vento-item">
                        <strong>Direzione:</strong> {vento.direzione.direzione} ({vento.direzione.gradi}°)<br/>
                        <strong>Nome:</strong> {vento.direzione.nome}<br/>
                        <strong>Provenienza:</strong> {vento.direzione.provenienza}<br/>
                        <strong>Caratteristiche:</strong> {vento.direzione.caratteristiche}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* POSIZIONE DOS - FIX ✅ */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">📍</div>
              <h4>Posizione DOS</h4>
            </div>
            <div className="step-content">
              <div className="info-box">
                {!posizioneDOS ? (
                  <div style={{padding: '20px', textAlign: 'center'}}>
                    <p style={{color: '#e74c3c', marginBottom: '10px'}}>
                      ❌ Nessuna coordinata GPS rilevata
                    </p>
                    <p style={{color: '#aaa', fontSize: '13px'}}>
                      Vai in <strong>Coordinate</strong> → <strong>Acquisisci GPS</strong> per rilevare la posizione DOS
                    </p>
                  </div>
                ) : (
                  <div className="coordinate-info">
                    {posizioneDOS.titolo && (
                      <div className="coord-item" style={{
                        background: 'rgba(255,107,53,0.1)',
                        padding: '10px',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        borderLeft: '4px solid #ff6b35'
                      }}>
                        <strong style={{color: '#ff6b35'}}>📍 {posizioneDOS.titolo}</strong>
                      </div>
                    )}
                    <div className="coord-item">
                      <strong>Latitudine:</strong> {typeof posizioneDOS.lat === 'number' 
                        ? posizioneDOS.lat.toFixed(7) 
                        : posizioneDOS.lat}°
                    </div>
                    <div className="coord-item">
                      <strong>Longitudine:</strong> {typeof posizioneDOS.lon === 'number' 
                        ? posizioneDOS.lon.toFixed(7) 
                        : posizioneDOS.lon}°
                    </div>
                    {posizioneDOS.altitude && (
                      <div className="coord-item">
                        <strong>Quota:</strong> {Math.round(posizioneDOS.altitude)}m
                      </div>
                    )}
                    {posizioneDOS.timestamp && (
                      <div className="coord-item" style={{color: '#aaa', fontSize: '12px'}}>
                        🕐 Rilevata: {posizioneDOS.timestamp}
                      </div>
                    )}
                    {posizioneDOS.note && (
                      <div className="coord-item" style={{
                        marginTop: '10px',
                        padding: '10px',
                        background: 'rgba(0,0,0,0.2)',
                        borderRadius: '6px'
                      }}>
                        <strong>Note:</strong> {posizioneDOS.note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ISTRUZIONI ALL'AVVICINAMENTO */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">✈️</div>
              <h4>Istruzioni all'avvicinamento</h4>
            </div>
            <div className="step-content">
              <div className="radio-message dos">
                <div className="message-label">📡 DOS</div>
                <div className="message-text">
                  <strong>{callsignMezzo} {callsignDOS}</strong> avvicinamento approvato, riportate un primo al lancio.
                </div>
              </div>
            </div>
          </div>

          {/* Bottone chiudi */}
          <div className="modal-footer-actions">
            <button className="btn btn-primary" onClick={onClose}>
              ✅ Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComunicazioniSicurezzaModal;
